/**
 * Terms of Service Screen
 * 
 * Displays SuperJarvis terms of service and usage guidelines.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, AlertTriangle, CheckCircle, Shield } from 'lucide-react';

const TermsScreen: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#0a0a0f] text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/30 border-b border-cyan-400/10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 rounded-xl bg-cyan-400/10 hover:bg-cyan-400/20 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-cyan-400" />
          </button>
          <div>
            <h1 className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
              Terms of Service
            </h1>
            <p className="text-sm text-gray-400">Last updated: January 31, 2025</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Key Points Summary */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-green-400/10 to-green-400/5 rounded-xl border border-green-400/20 p-4">
            <CheckCircle className="w-6 h-6 text-green-400 mb-2" />
            <h3 className="font-medium mb-1">What You Can Do</h3>
            <p className="text-xs text-gray-400">Use SuperJarvis for personal and business productivity tasks</p>
          </div>
          
          <div className="bg-gradient-to-br from-yellow-400/10 to-yellow-400/5 rounded-xl border border-yellow-400/20 p-4">
            <AlertTriangle className="w-6 h-6 text-yellow-400 mb-2" />
            <h3 className="font-medium mb-1">Use Responsibly</h3>
            <p className="text-xs text-gray-400">Don't abuse the AI or use it for harmful purposes</p>
          </div>
          
          <div className="bg-gradient-to-br from-cyan-400/10 to-cyan-400/5 rounded-xl border border-cyan-400/20 p-4">
            <Shield className="w-6 h-6 text-cyan-400 mb-2" />
            <h3 className="font-medium mb-1">We Protect You</h3>
            <p className="text-xs text-gray-400">Your data is encrypted and you control what's shared</p>
          </div>
        </section>

        {/* Full Terms */}
        <section className="bg-white/5 rounded-2xl border border-white/10 p-6 space-y-6">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-6 h-6 text-cyan-400" />
            <h2 className="text-lg font-semibold">Terms of Service</h2>
          </div>
          
          <div className="space-y-6 text-sm text-gray-300">
            <div>
              <h3 className="font-medium text-white mb-2">1. Acceptance of Terms</h3>
              <p>
                By accessing or using SuperJarvis, you agree to be bound by these Terms of Service. 
                If you do not agree to these terms, please do not use the service.
              </p>
            </div>

            <div>
              <h3 className="font-medium text-white mb-2">2. Description of Service</h3>
              <p>
                SuperJarvis is an AI-powered executive companion that helps you manage tasks, 
                automate workflows, and increase productivity. The service includes:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-gray-400">
                <li>AI conversation and task execution</li>
                <li>Integration with third-party services (Gmail, Calendar, etc.)</li>
                <li>Custom app creation and automation</li>
                <li>Project and task management</li>
                <li>Shadow Mode learning capabilities</li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium text-white mb-2">3. User Accounts</h3>
              <p>
                You are responsible for maintaining the security of your account credentials. 
                You agree to:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-gray-400">
                <li>Provide accurate account information</li>
                <li>Keep your login credentials secure</li>
                <li>Notify us immediately of any unauthorized access</li>
                <li>Accept responsibility for all activities under your account</li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium text-white mb-2">4. Acceptable Use</h3>
              <p>You agree NOT to use SuperJarvis to:</p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-gray-400">
                <li>Violate any laws or regulations</li>
                <li>Harm, harass, or deceive others</li>
                <li>Generate spam, malware, or malicious content</li>
                <li>Attempt to bypass security measures</li>
                <li>Scrape or harvest data without permission</li>
                <li>Impersonate others or misrepresent yourself</li>
                <li>Use the AI for illegal automation</li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium text-white mb-2">5. AI Capabilities & Limitations</h3>
              <p>
                SuperJarvis uses advanced AI to assist you, but you acknowledge that:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-gray-400">
                <li>AI responses may not always be accurate</li>
                <li>You should verify important information</li>
                <li>The AI acts on your instructions — you are responsible for outcomes</li>
                <li>Automated actions require your authorization</li>
                <li>Self-healing (Phoenix Protocol) may modify execution paths</li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium text-white mb-2">6. Third-Party Integrations</h3>
              <p>
                When you connect third-party services (Google, Slack, etc.), you authorize 
                SuperJarvis to access and act on those services according to your instructions. 
                We store tokens securely and only access what's needed for your tasks.
              </p>
            </div>

            <div>
              <h3 className="font-medium text-white mb-2">7. Intellectual Property</h3>
              <p>
                You retain ownership of content you create. SuperJarvis and its technology 
                are owned by SuperJarvis AI. You grant us a license to use your content 
                solely to provide the service.
              </p>
            </div>

            <div>
              <h3 className="font-medium text-white mb-2">8. Privacy & Data</h3>
              <p>
                Your privacy matters. Please review our <button onClick={() => navigate('/privacy')} className="text-cyan-400 hover:underline">Privacy Policy</button> for 
                details on how we collect, use, and protect your data.
              </p>
            </div>

            <div>
              <h3 className="font-medium text-white mb-2">9. Service Availability</h3>
              <p>
                We strive for 99.9% uptime but cannot guarantee uninterrupted service. 
                We may modify, suspend, or discontinue features with reasonable notice.
              </p>
            </div>

            <div>
              <h3 className="font-medium text-white mb-2">10. Limitation of Liability</h3>
              <p>
                SuperJarvis is provided "as is." We are not liable for indirect, incidental, 
                or consequential damages arising from your use of the service. Our total 
                liability is limited to the amount you paid in the past 12 months.
              </p>
            </div>

            <div>
              <h3 className="font-medium text-white mb-2">11. Termination</h3>
              <p>
                You may cancel your account at any time. We may terminate accounts that 
                violate these terms. Upon termination, you may request export of your data 
                within 30 days.
              </p>
            </div>

            <div>
              <h3 className="font-medium text-white mb-2">12. Changes to Terms</h3>
              <p>
                We may update these terms. Material changes will be communicated via email 
                or in-app notification at least 30 days before taking effect.
              </p>
            </div>

            <div>
              <h3 className="font-medium text-white mb-2">13. Governing Law</h3>
              <p>
                These terms are governed by the laws of the State of Delaware, USA, 
                without regard to conflict of law principles.
              </p>
            </div>

            <div>
              <h3 className="font-medium text-white mb-2">14. Contact</h3>
              <p>
                Questions about these terms? Contact us at: <br />
                <span className="text-cyan-400">legal@superjarvis.ai</span>
              </p>
            </div>
          </div>
        </section>

        {/* Accept Button */}
        <div className="text-center">
          <button 
            onClick={() => navigate(-1)}
            className="px-8 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 transition-all font-medium shadow-lg shadow-cyan-500/25"
          >
            I Understand & Accept
          </button>
        </div>

        <p className="text-center text-xs text-gray-500">
          Last updated: January 31, 2025 • Contact: legal@superjarvis.ai
        </p>
      </main>
    </div>
  );
};

export default TermsScreen;
